import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { getEmployee, getEmployeeAppraisalHistory } from '../../services/employeeService';
import { Card, Row, Col, Badge, Button, Spinner, Container, Tab, Nav } from 'react-bootstrap';
import { FaFilePdf, FaImage, FaDownload, FaPen } from 'react-icons/fa';
import Layout from '../../components/Layout';
import TopBar from '../../components/TopBar';
import { stringToColor, getInitials } from '../../utility';
import { API_COLORS } from '../../constants/colors';
import { baseURL, imageUrl } from '../../services/api';
import moment from 'moment';
import { AuthContext } from '../../context/AuthContext';

const MyProfile = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [appraisalHistory, setAppraisalHistory] = useState([]);
  const [error, setError] = useState('');

  const fetchAppraisalHistory = async (employeeId) => {
    try {
      setLoadingHistory(true);
      const response = await getEmployeeAppraisalHistory(employeeId);
      if (response.data) {
        setAppraisalHistory(response.data.data || []);
      }
      setError('');
    } catch (error) {
      console.error('Error fetching appraisal history:', error);
      setError('Failed to load appraisal history');
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    const fetchEmployee = async () => {
      if (!user?.id) return;
      
      setLoading(true);
      try {
        const res = await getEmployee(user.id);
        setEmployee(res.data);
        // Fetch appraisal history after employee data is loaded
        await fetchAppraisalHistory(res.data.id);
      } catch (error) {
        console.error('Error fetching employee:', error);
        setError('Failed to load employee data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchEmployee();
  }, [user?.id]);

  if (loading) {
    return (
      <Layout>
        <TopBar title="My Profile" />
        <Container fluid>
          <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
            <Spinner animation="border" role="status" />
          </div>
        </Container>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <TopBar title="My Profile" />
        <Container fluid>
          <div className="alert alert-danger">{error}</div>
        </Container>
      </Layout>
    );
  }

  if (!employee) {
    return (
      <Layout>
        <TopBar title="My Profile" />
        <Container fluid>
          <div className="alert alert-warning">No profile data found</div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <TopBar title="My Profile" />
      <Container fluid>
        <Container fluid className="mt-4">
            <Row className="g-3">
              <Col md={4}>
                <Card className="shadow-sm border-0 h-100" style={{ borderRadius: '1rem' }}>
                  <Card.Body>
                    <Row className="align-items-center">
                      <Col md={4} className="text-center">
                        {employee.profile_image ? (
                          <img
                            src={`${imageUrl}/${employee.profile_image}`}
                            alt="Profile"
                            className="img-fluid rounded-circle mb-2"
                            style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                          />
                        ) : (
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center mb-2"
                            style={{
                              width: '100px',
                              height: '100px',
                              backgroundColor: stringToColor(employee.name),
                              color: 'white',
                              fontSize: '24px'
                            }}
                          >
                            {getInitials(employee.name)}
                          </div>
                        )}
                        <Badge bg={employee.status === 'Active' ? 'success' : 'secondary'} className="mt-2">
                          {employee.status || 'Inactive'}
                        </Badge>
                      </Col>
                      <Col md={8}>
                        <h5 className="mb-1">{employee.name}</h5>
                        <p className="text-muted mb-1">{employee.role}</p>
                        {employee.emp_code && (
                          <p className="text-muted mb-1">
                            <strong>Employee Code:</strong> {employee.emp_code}
                          </p>
                        )}
                        {employee.email && (
                          <p className="text-muted mb-1">
                            <strong>Email:</strong> {employee.email}
                          </p>
                        )}
                        {employee.phone && (
                          <p className="text-muted mb-1">
                            <strong>Phone:</strong> {employee.phone}
                          </p>
                        )}
                        <p className="mb-2"><strong>Department:</strong> {employee.department?.name || '-'}</p>
                        <p className="mb-2"><strong>Designation:</strong> {employee.designation?.name || '-'}</p>
                        
                        <Row className="mt-4">
                          <Col className="d-grid">
                          
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col md={4}>
                <Card className="shadow-sm border-0 h-100" style={{ borderRadius: '1rem' }}>
                  <Card.Header className="bg-light">
                    <h6 className="mb-0">Salary Information</h6>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <p className="mb-2"><strong>Salary Type:</strong> {employee.salary_type || '-'}</p>
                        <p className="mb-2"><strong>Base Salary:</strong> {employee.base_salary ? `₹${Number(employee.base_salary).toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '-'}</p>
                      </Col>
                      <Col md={6}>
                        <p className="mb-2"><strong>Hourly Rate:</strong> {employee.hourly_rate ? `₹${Number(employee.hourly_rate).toLocaleString('en-IN', { maximumFractionDigits: 2 })}/hr` : '-'}</p>
                        <p className="mb-2"><strong>Payment Method:</strong> {employee.payment_method || '-'}</p>
                      </Col>
                    </Row>
                    {employee.allowances && employee.allowances.length > 0 && (
                      <div className="mt-3">
                        <h6>Allowances:</h6>
                        <ul className="list-unstyled">
                          {employee.allowances.map((allowance, index) => (
                            <li key={index}>
                              {allowance.name}: ₹{Number(allowance.amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })} {allowance.type === 'percentage' ? `(${allowance.amount}%)` : ''}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              
              <Col md={4}>
                <Card className="shadow-sm border-0 h-100" style={{ borderRadius: '1rem' }}>
                  <Card.Header className="bg-light">
                    <h6 className="mb-0">Personal Information</h6>
                  </Card.Header>
                  <Card.Body>
                    <div className="ps-4">
                      <p className="mb-2"><strong>DOB:</strong> {employee?.dob ? moment(employee.dob).isValid() ? moment(employee.dob).format('DD-MMMM-YYYY') : 'N/A' : 'N/A'}</p>
                      <p className="mb-2"><strong>Joining Date:</strong> {employee?.joining_date ? moment(employee.joining_date).isValid() ? moment(employee.joining_date).format('DD-MMMM-YYYY') : 'N/A' : 'N/A'}</p>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* ID Documents Section */}
            <Row className="mt-4">
              <Col md={12}>
                <Card className="shadow-sm border-0 h-100" style={{ borderRadius: '1rem' }}>
                  <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                    <h6 className="mb-0 d-flex align-items-center">
                      <i className="fas fa-id-card me-2 text-primary"></i>
                      ID Documents
                    </h6>
                    <Badge bg="light" text="primary" className="border border-primary">
                      {employee.ids?.length || 0} {employee.ids?.length === 1 ? 'Document' : 'Documents'}
                    </Badge>
                  </Card.Header>
                  <Card.Body className="p-0">
                    {employee.ids?.length > 0 ? (
                      <div className="p-3">
                        <div className="table-responsive">
                          <table className="table table-hover align-middle mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>Type</th>
                                <th>Number</th>
                                <th>Status</th>
                                <th>Expiry</th>
                                <th>Document</th>
                              </tr>
                            </thead>
                            <tbody>
                              {employee.ids.map((id, i) => (
                                <tr key={i}>
                                  <td>
                                    <span className="badge bg-primary bg-opacity-10 text-primary">
                                      {id.type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'}
                                    </span>
                                  </td>
                                  <td>
                                    <span className="text-muted">{id.number || 'N/A'}</span>
                                  </td>
                                  <td>
                                    {id.expiry_date ? (
                                      <span className={`badge ${new Date(id.expiry_date) < new Date() ? 'bg-danger' : 'bg-success'} bg-opacity-10`}>
                                        {new Date(id.expiry_date) < new Date() ? 'Expired' : 'Valid'}
                                      </span>
                                    ) : 'N/A'}
                                  </td>
                                  <td>
                                    {id.expiry_date ? (
                                      <span className="text-muted small">
                                        {new Date(id.expiry_date).toLocaleDateString()}
                                      </span>
                                    ) : 'N/A'}
                                  </td>
                                  <td>
                                    {id.image ? (
                                      <div className="d-flex align-items-center">
                                        <a 
                                          href={`${imageUrl}/${id.image}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-decoration-none me-2"
                                          download
                                        >
                                          {id.image.endsWith('.pdf') || id.image_type === 'application/pdf' ? (
                                            <div className="text-center">
                                              <FaFilePdf className="text-danger d-block mx-auto" size={24} />
                                              <small className="text-muted">PDF</small>
                                            </div>
                                          ) : (
                                            <div className="position-relative" style={{ width: '60px' }}>
                                              <img 
                                                src={`${imageUrl}/${id.image}`}
                                                className="img-thumbnail"
                                                alt={`${id.type} document`}
                                                style={{ 
                                                  width: '100%',
                                                  height: '50px',
                                                  objectFit: 'cover'
                                                }}
                                                onError={(e) => {
                                                  e.target.style.display = 'none';
                                                  if (e.target.nextElementSibling) {
                                                    e.target.nextElementSibling.style.display = 'flex';
                                                  }
                                                }}
                                              />
                                              <div 
                                                className="d-none align-items-center justify-content-center"
                                                style={{
                                                  width: '100%',
                                                  height: '50px',
                                                  backgroundColor: '#f8f9fa',
                                                  border: '1px solid #dee2e6',
                                                  borderRadius: '0.25rem'
                                                }}
                                              >
                                                <FaImage className="text-muted" />
                                              </div>
                                            </div>
                                          )}
                                        </a>
                                        <a 
                                          href={`${imageUrl}/${id.image}`}
                                          target="_blank"
                                          download
                                          className="btn btn-sm btn-outline-primary"
                                          title="Download document"
                                        >
                                          <FaDownload />
                                        </a>
                                      </div>
                                    ) : 'N/A'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center p-5">
                        <i className="fas fa-id-card fa-3x text-muted mb-3"></i>
                        <p className="text-muted mb-0">No ID documents available</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Employee History Section */}
            <Row className="mt-4">
              <Col md={12}>
                <Card className="shadow-sm border-0 h-100" style={{ borderRadius: '1rem' }}>
                  <Card.Header className="bg-light">
                    <h6 className="mb-0">Employee History</h6>
                    <p className="text-muted mb-0">Track all important changes and activities</p>
                  </Card.Header>
                  <Card.Body>
                    <Tab.Container defaultActiveKey="appraisal">
                      <Tab.Content>
                        <Tab.Pane eventKey="appraisal">
                          <div className="table-responsive">
                            <table className="table table-hover">
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th>Employee Type</th>
                                  <th>Previous Salary</th>
                                  <th>New Salary</th>
                                  <th>Increment Amount</th>
                                  <th>Designation</th>
                                </tr>
                              </thead>
                              <tbody>
                                {loadingHistory ? (
                                  <tr>
                                    <td colSpan="6" className="text-center py-4">
                                      <Spinner animation="border" size="sm" className="me-2" />
                                      Loading history...
                                    </td>
                                  </tr>
                                ) : appraisalHistory.length > 0 ? (
                                  appraisalHistory.map((history, index) => (
                                    <tr key={index}>
                                      <td>{moment(history.created_at).format('DD MMM YYYY')}</td>
                                      <td>{history.employee_type || '-'}</td>
                                      <td>
                                        {history.previous_salary 
                                          ? `₹${parseFloat(history.previous_salary).toLocaleString()}`
                                          : '-'}
                                      </td>
                                      <td>
                                        {history.new_salary 
                                          ? `₹${parseFloat(history.new_salary).toLocaleString()}`
                                          : '-'}
                                      </td>
                                      <td>
                                        {history.new_salary && history.previous_salary
                                          ? `₹${(parseFloat(history.new_salary) - parseFloat(history.previous_salary)).toLocaleString()}`
                                          : '-'}
                                      </td>
                                      <td>
                                        {history.new_designation?.name || 
                                         history.previous_designation?.name || '-'}
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan="6" className="text-center py-4 text-muted">
                                      No appraisal history found
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </Tab.Pane>
                      </Tab.Content>
                    </Tab.Container>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
      </Container>
    </Layout>
  );
};

export default MyProfile;
